<template>
  <TransitionGroup
    tag="div"
    :enter-from-class="$style.collapsed"
    :leave-to-class="$style.collapsed"
    :enter-active-class="$style.collapsibleAnimating"
    :leave-active-class="$style.collapsibleAnimating"
    test-id="column-filter-builder"
    @keydown.tab.exact="onTab"
  >
    <div
      v-for="view in rowViews"
      :key="view.key"
      :class="[$style.collapsible, $style.row]"
      @focusout="onRowFocusOut(view.key, $event)"
    >
      <div :class="[$style.collapsibleContent, $style.content]">
        <ConditionJoinToggle
          v-if="view.index > 0"
          :class="$style.join"
          :model-value="view.row.join"
          @update:model-value="(join) => onJoin(view.index, join)"
        />
        <div :class="$style.operatorLine">
          <ConditionOperatorSelect
            :class="$style.operator"
            :options="operatorOptions"
            :model-value="view.row.operator"
            :removable="view.valueless"
            @update:model-value="(operator) => onOperator(view.index, operator)"
            @remove="onRemove(view.index)"
          />
        </div>
        <template v-if="!view.valueless">
          <DateConditionInput
            v-if="dateFormat"
            :ref="(el) => setEditor(view.key, el)"
            :kind="dateFormat.valueKind"
            :date-format="dateFormat.dateFormat"
            :time-format="dateFormat.timeFormat"
            :date-precision="dateFormat.datePrecision"
            :time-precision="dateFormat.timePrecision"
            :range="view.range"
            :removable="view.complete"
            :alert="view.rangeError"
            :start="view.row.term"
            :end="view.row.termTo"
            @update:start="(value) => onTerm(view.index, value, 'term')"
            @update:end="(value) => onTerm(view.index, value, 'termTo')"
            @remove="onRemove(view.index)"
            @submit="onSubmit"
          />
          <ConditionTextInput
            v-else
            :ref="(el) => setEditor(view.key, el)"
            :numeric="kind === 'number'"
            :range="view.range"
            :removable="view.complete"
            :alert="view.rangeError"
            :start="view.row.term"
            :end="view.row.termTo"
            @update:start="(value) => onTerm(view.index, value, 'term')"
            @update:end="(value) => onTerm(view.index, value, 'termTo')"
            @remove="onRemove(view.index)"
            @submit="onSubmit"
          />
          <SCAHelperText v-if="view.rangeError" intent="alert" :show-icon="false" test-id="column-filter-range-error">
            {{ rangeErrorText }}
          </SCAHelperText>
        </template>
      </div>
    </div>
  </TransitionGroup>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from "vue";
import { computed, nextTick, reactive, watch } from "vue";

import { $t } from "../../../i18n";
import SCAHelperText from "../../SCAHelperText.vue";
import DateConditionInput from "../date/DateConditionInput.vue";
import type { LogicalOperator } from "../filterModel";
import { FilterOperator } from "../filterModel";
import type { ConditionFilterKind, DateFilterFormat, OperatorOption } from "../types";
import type { ConditionRow } from "./conditionChain";
import { isConditionRowComplete } from "./conditionChain";
import ConditionJoinToggle from "./ConditionJoinToggle.vue";
import ConditionOperatorSelect from "./ConditionOperatorSelect.vue";
import ConditionTextInput from "./ConditionTextInput.vue";
import { isValuelessOperator } from "./operators";
import { compareRangeTerms, isInvertedRange } from "./rangeOrder";
import { isLastTabStop } from "./tabStops";
import type { ConditionTermField } from "./useConditionChain";
import { useConditionChain } from "./useConditionChain";

const props = defineProps<{
  kind: ConditionFilterKind;
  operatorOptions: readonly OperatorOption[];
  defaultOperator: FilterOperator;
  /** Required for `kind: "date"`. */
  dateFormat?: DateFilterFormat;
  /** The complete rows of the current filter. */
  rows: readonly ConditionRow[];
}>();

const emit = defineEmits<{
  change: [rows: ConditionRow[]];
  submit: [];
}>();

const compare = computed(() => compareRangeTerms(props.kind, props.dateFormat?.valueKind));

const chain = useConditionChain({ defaultOperator: () => props.defaultOperator, compare });

/** Rows edited since focus left them: an inverted range there may be half typed (To 1 on the way to 1000). */
const typingKeys = reactive(new Set<number>());

const rowViews = computed(() =>
  chain.rows.value.map((row, index) => {
    const key = chain.keys.value[index];
    return {
      row,
      index,
      key,
      valueless: isValuelessOperator(row.operator),
      complete: isConditionRowComplete(row),
      range: row.operator === FilterOperator.IN_RANGE,
      rangeError: isInvertedRange(row, compare.value) && !typingKeys.has(key),
    };
  }),
);

const rangeErrorText = computed(() =>
  props.kind === "date" ? $t("columnFilter.range.invalidDate") : $t("columnFilter.range.invalidNumber"),
);

let lastEmittedKey: string | undefined;

function emitChange() {
  const rows = chain.completeRows.value;
  const key = JSON.stringify(rows);
  if (key === lastEmittedKey) return;
  lastEmittedKey = key;
  emit("change", rows);
}

function onOperator(index: number, operator: FilterOperator) {
  chain.setOperator(index, operator);
  emitChange();
  const target = isValuelessOperator(operator) ? chain.rows.value.length - 1 : index;
  void nextTick(() => focusEditor(target));
}

function onTerm(index: number, value: string, field: ConditionTermField) {
  typingKeys.add(chain.keys.value[index]);
  chain.setTerm(index, value, field);
  emitChange();
}

function onRowFocusOut(key: number, event: FocusEvent) {
  const row = event.currentTarget as HTMLElement;
  if (!row.contains(event.relatedTarget as Node | null)) typingKeys.delete(key);
}

/** An inverted range keeps the popup open on Enter or Tab, so its message can be read. */
function onSubmit() {
  typingKeys.clear();
  if (chain.rows.value.some((row) => isInvertedRange(row, compare.value))) return;
  emit("submit");
}

function onJoin(index: number, join: LogicalOperator) {
  chain.setJoin(index, join);
  emitChange();
}

function onRemove(index: number) {
  chain.removeRow(index);
  emitChange();
}

watch(
  () => props.rows,
  (rows) => {
    const key = JSON.stringify(rows);
    if (key === lastEmittedKey) return;
    lastEmittedKey = key;
    chain.replaceAll(rows);
  },
  { deep: true, immediate: true },
);

interface Focusable {
  focus: () => void;
}

const editors = new Map<number, Focusable>();

function setEditor(key: number, el: Element | ComponentPublicInstance | null) {
  if (el) editors.set(key, el as unknown as Focusable);
  else editors.delete(key);
}

function onTab(event: KeyboardEvent) {
  if (event.defaultPrevented || !isLastTabStop(event.currentTarget as HTMLElement, event.target as Element)) return;
  event.preventDefault();
  onSubmit();
}

function focusEditor(index: number) {
  editors.get(chain.keys.value[index])?.focus();
}

async function focus() {
  await nextTick();
  focusEditor(chain.focusIndex.value);
}

defineExpose({ focus });
</script>

<style module lang="scss">
@use "../../../styles/collapse.scss" as CollapseMixins;

@include CollapseMixins.collapse-transitions;

.row {
  --column-filter-remove-visibility: hidden;
}

.row:hover,
.row:focus-within {
  --column-filter-remove-visibility: visible;
}

.content {
  display: flex;
  flex-direction: column;
}

.join {
  align-self: flex-start;
}

.operatorLine {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
}

.operator {
  flex: 1;
  min-width: 0;
}
</style>
